// Hover-intent timings (ms). Tuned to reject incidental sweeps:
//   OPEN delay  → mouse must rest on a trigger this long before opening.
//   CLOSE delay → menu stays open this long after the mouse leaves so
//                 brief exits (e.g. crossing a gap to the dropdown body)
//                 don't dismiss it.
// When another mega menu is already open, the open delay is bypassed so
// horizontal movement between triggers feels instant once engaged.
const HOVER_OPEN_DELAY_MS = 150;
const HOVER_CLOSE_DELAY_MS = 300;

class HoverMenu extends HTMLElement {
  constructor() {
    super();
    this.hoverMenu = this.querySelector('[data-hover-menu]');
    this.trigger = this.querySelector('.mega-menu__trigger, .secondary-menu__trigger');
    this.content = this.querySelector('[data-menu-content]');
    this.openTimeout = null;
    this.closeTimeout = null;
    this.isHovering = false;
    this.isTransparentHeader = false;
  }

  connectedCallback() {
    if (!this.hoverMenu || !this.trigger || !this.content) return;
    
    this.checkTransparentHeader();
    this.setupEventListeners();
    this.setupAccessibility();
    this.initializeMenuState();
  }

  setupEventListeners() {
    // Mouse events for hover functionality
    this.hoverMenu.addEventListener('mouseenter', this.handleMouseEnter.bind(this));
    this.hoverMenu.addEventListener('mouseleave', this.handleMouseLeave.bind(this));
    
    // Keyboard events for accessibility
    this.trigger.addEventListener('keydown', this.handleKeyDown.bind(this));
    this.trigger.addEventListener('focus', this.handleFocus.bind(this));
    this.trigger.addEventListener('blur', this.handleBlur.bind(this));

    // Add keyboard navigation for menu items
    this.content.addEventListener('keydown', this.handleMenuKeyDown.bind(this));
    this.content.addEventListener('focusout', this.handleMenuFocusOut.bind(this));

    // Close menu when clicking outside
    document.addEventListener('click', this.handleOutsideClick.bind(this));
    
    // Handle secondary submenus if present
    this.setupSecondarySubmenus();
  }

  checkTransparentHeader() {
    const header = document.querySelector('.section-header--transparent');
    this.isTransparentHeader = !!header;
    
    if (this.isTransparentHeader) {
      // Listen for scroll events to handle transparent header state changes
      window.addEventListener('scroll', this.handleTransparentHeaderScroll.bind(this));
    }
  }

  handleTransparentHeaderScroll() {
    // Close menu when scrolling to prevent positioning issues
    if (this.isMenuOpen()) {
      this.closeMenu();
    }
  }

  setupAccessibility() {
    // Set up ARIA attributes
    this.trigger.setAttribute('aria-expanded', 'false');
    this.trigger.setAttribute('aria-haspopup', 'true');
    this.content.setAttribute('aria-hidden', 'true');
    
    // Generate unique IDs if not present
    if (!this.content.id) {
      this.content.id = `mega-menu-content-${Math.random().toString(36).substr(2, 9)}`;
    }
    this.trigger.setAttribute('aria-controls', this.content.id);
  }

  initializeMenuState() {
    // Ensure menu is closed initially
    this.hoverMenu.removeAttribute('open');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.content.setAttribute('aria-hidden', 'true');
  }

  handleMouseEnter() {
    // Cancel any pending close from a brief earlier exit.
    clearTimeout(this.closeTimeout);
    this.isHovering = true;

    // Roll-over: if another mega menu is already showing, the user is
    // already "engaged" with the nav — open this one immediately so
    // horizontal scanning between triggers stays fluid.
    const otherOpen = document.querySelector('hover-menu [data-hover-menu][open]');
    if (otherOpen && otherOpen !== this.hoverMenu) {
      clearTimeout(this.openTimeout);
      this.openMenu();
      return;
    }

    // Otherwise require an intent rest: the user has to actually
    // pause on this trigger, not just sweep across it.
    clearTimeout(this.openTimeout);
    this.openTimeout = setTimeout(() => {
      if (this.isHovering) {
        this.openMenu();
      }
    }, HOVER_OPEN_DELAY_MS);
  }

  handleMouseLeave() {
    this.isHovering = false;
    // Cancel any pending open if the user left before the rest interval.
    clearTimeout(this.openTimeout);

    // Delay the close so brief exits don't dismiss an intended menu.
    clearTimeout(this.closeTimeout);
    this.closeTimeout = setTimeout(() => {
      if (!this.isHovering) {
        this.closeMenu();
      }
    }, HOVER_CLOSE_DELAY_MS);
  }

  handleKeyDown(event) {
    switch (event.key) {
      case 'ArrowDown':
      case 'Enter':
      case ' ':
        if (!this.isMenuOpen()) {
          event.preventDefault();
          this.openMenu();
          this.focusFirstMenuItem();
        } else if (event.key === 'ArrowDown') {
          event.preventDefault();
          this.focusNextMenuItem();
        }
        break;
      case 'ArrowUp':
        if (this.isMenuOpen()) {
          event.preventDefault();
          this.focusPreviousMenuItem();
        }
        break;
      case 'ArrowRight':
        if (this.isMenuOpen()) {
          event.preventDefault();
          this.focusNextColumn();
        }
        break;
      case 'ArrowLeft':
        if (this.isMenuOpen()) {
          event.preventDefault();
          this.focusPreviousColumn();
        }
        break;
      case 'Tab':
        if (!this.isMenuOpen()) {
          // Open menu on tab if not already open
          this.openMenu();
        }
        break;
      case 'Escape':
        if (this.isMenuOpen()) {
          event.preventDefault();
          this.closeMenu();
          this.trigger.focus();
        }
        break;
    }
  }

  handleFocus() {
    // Don't auto-open on focus, only on explicit keyboard interaction
  }

  handleBlur(event) {
    // Close menu if focus moves outside the hover menu
    setTimeout(() => {
      if (!this.hoverMenu.contains(document.activeElement)) {
        this.closeMenu();
      }
    }, 0);
  }

  handleMenuKeyDown(event) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusNextMenuItem();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusPreviousMenuItem();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.focusNextColumn();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this.focusPreviousColumn();
        break;
      case 'Home':
        event.preventDefault();
        this.focusFirstMenuItem();
        break;
      case 'End':
        event.preventDefault();
        this.focusLastMenuItem();
        break;
      case 'Escape':
        event.preventDefault();
        this.closeMenu();
        this.trigger.focus();
        break;
    }
  }

  handleMenuFocusOut(event) {
    // Check if focus is moving outside the menu
    setTimeout(() => {
      if (!this.hoverMenu.contains(document.activeElement)) {
        this.closeMenu();
      }
    }, 0);
  }

  focusLastMenuItem() {
    const allLinks = Array.from(this.content.querySelectorAll('a'));
    const lastLink = allLinks[allLinks.length - 1];
    if (lastLink) {
      lastLink.focus();
    }
  }

  handleOutsideClick(event) {
    if (!this.hoverMenu.contains(event.target)) {
      this.closeMenu();
    }
  }

  openMenu() {
    this.hoverMenu.setAttribute('open', '');
    this.trigger.setAttribute('aria-expanded', 'true');
    this.content.setAttribute('aria-hidden', 'false');
    
    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('hover-menu:open', {
      bubbles: true,
      detail: { menu: this.hoverMenu }
    }));
  }

  closeMenu() {
    this.hoverMenu.removeAttribute('open');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.content.setAttribute('aria-hidden', 'true');
    
    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('hover-menu:close', {
      bubbles: true,
      detail: { menu: this.hoverMenu }
    }));
  }

  isMenuOpen() {
    return this.hoverMenu.hasAttribute('open');
  }

  focusFirstMenuItem() {
    const firstLink = this.content.querySelector('a');
    if (firstLink) {
      firstLink.focus();
    }
  }

  focusNextMenuItem() {
    const currentFocus = document.activeElement;
    const allLinks = Array.from(this.content.querySelectorAll('a'));
    const currentIndex = allLinks.indexOf(currentFocus);
    
    if (currentIndex >= 0 && currentIndex < allLinks.length - 1) {
      allLinks[currentIndex + 1].focus();
    } else if (currentIndex === -1) {
      // If no current focus, focus first item
      this.focusFirstMenuItem();
    }
  }

  focusPreviousMenuItem() {
    const currentFocus = document.activeElement;
    const allLinks = Array.from(this.content.querySelectorAll('a'));
    const currentIndex = allLinks.indexOf(currentFocus);
    
    if (currentIndex > 0) {
      allLinks[currentIndex - 1].focus();
    } else if (currentIndex === 0) {
      // If at first item, focus trigger
      this.trigger.focus();
    }
  }

  focusNextColumn() {
    const currentFocus = document.activeElement;
    const currentLi = currentFocus.closest('li');
    if (!currentLi) return;
    
    // Find the parent ul and then the next sibling li at the same level
    const parentUl = currentLi.parentElement;
    const topLevelLis = Array.from(this.content.querySelectorAll('.mega-menu__list > li'));
    const currentTopLevelLi = currentLi.closest('.mega-menu__list > li');
    const currentColumnIndex = topLevelLis.indexOf(currentTopLevelLi);
    
    if (currentColumnIndex >= 0 && currentColumnIndex < topLevelLis.length - 1) {
      const nextColumn = topLevelLis[currentColumnIndex + 1];
      const firstLink = nextColumn.querySelector('a');
      if (firstLink) {
        firstLink.focus();
      }
    }
  }

  focusPreviousColumn() {
    const currentFocus = document.activeElement;
    const currentLi = currentFocus.closest('li');
    if (!currentLi) return;
    
    const topLevelLis = Array.from(this.content.querySelectorAll('.mega-menu__list > li'));
    const currentTopLevelLi = currentLi.closest('.mega-menu__list > li');
    const currentColumnIndex = topLevelLis.indexOf(currentTopLevelLi);
    
    if (currentColumnIndex > 0) {
      const previousColumn = topLevelLis[currentColumnIndex - 1];
      const firstLink = previousColumn.querySelector('a');
      if (firstLink) {
        firstLink.focus();
      }
    }
  }

  setupSecondarySubmenus() {
    const submenus = this.querySelectorAll('[data-hover-submenu]');
    submenus.forEach(submenu => {
      const submenuTrigger = submenu.querySelector('.secondary-submenu__trigger');
      const submenuContent = submenu.querySelector('[data-submenu-content]');
      
      if (submenuTrigger && submenuContent) {
        let submenuHoverTimeout = null;
        let isSubmenuHovering = false;
        
        submenu.addEventListener('mouseenter', () => {
          clearTimeout(submenuHoverTimeout);
          isSubmenuHovering = true;
          submenu.setAttribute('open', '');
          submenuTrigger.setAttribute('aria-expanded', 'true');
          submenuContent.setAttribute('aria-hidden', 'false');
        });
        
        submenu.addEventListener('mouseleave', () => {
          isSubmenuHovering = false;
          submenuHoverTimeout = setTimeout(() => {
            if (!isSubmenuHovering) {
              submenu.removeAttribute('open');
              submenuTrigger.setAttribute('aria-expanded', 'false');
              submenuContent.setAttribute('aria-hidden', 'true');
            }
          }, this.isTransparentHeader ? 200 : 100);
        });
        
        // Accessibility
        submenuTrigger.setAttribute('aria-expanded', 'false');
        submenuTrigger.setAttribute('aria-haspopup', 'true');
        submenuContent.setAttribute('aria-hidden', 'true');
        
        if (!submenuContent.id) {
          submenuContent.id = `secondary-submenu-content-${Math.random().toString(36).substr(2, 9)}`;
        }
        submenuTrigger.setAttribute('aria-controls', submenuContent.id);
      }
    });
  }

  // Method to close menu (for external use)
  close() {
    this.closeMenu();
    // Also close any secondary submenus
    const openSubmenus = this.querySelectorAll('[data-hover-submenu][open]');
    openSubmenus.forEach(submenu => {
      submenu.removeAttribute('open');
      const trigger = submenu.querySelector('.secondary-submenu__trigger');
      const content = submenu.querySelector('[data-submenu-content]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (content) content.setAttribute('aria-hidden', 'true');
    });
  }
}

// Register the custom element
customElements.define('hover-menu', HoverMenu);

// Close all hover menus when another one opens
document.addEventListener('hover-menu:open', (event) => {
  const openingMenu = event.detail.menu;
  const allHoverMenus = document.querySelectorAll('hover-menu');
  
  allHoverMenus.forEach(menu => {
    const menuElement = menu.querySelector('[data-hover-menu]');
    if (menuElement && menuElement !== openingMenu) {
      menu.close();
    }
  });
});

// Lock page scroll while any subnav is open. Driven off the open/close events
// rather than a single menu so roll-overs between triggers (one closes as the
// next opens) keep the lock; it lifts only once no hover menu is open. The
// `mega-menu-open` body class is scoped to desktop in component-mega-menu.css.
function syncSubnavScrollLock() {
  const anyOpen = !!document.querySelector('hover-menu [data-hover-menu][open]');
  // Toggle on <html> as well as <body>: the document scrolls on the root
  // element here, so `overflow: hidden` on body alone does NOT lock the page.
  // The transparency rules key off body.mega-menu-open; the scroll lock keys
  // off html.mega-menu-open.
  document.body.classList.toggle('mega-menu-open', anyOpen);
  document.documentElement.classList.toggle('mega-menu-open', anyOpen);

  // Cap the open dropdown to the real space below the nav row so a tall subnav
  // scrolls internally with nothing left under the fold. Measure from the
  // navigation row's bottom — that's where the dropdown anchors (top: 100%), and
  // it accounts for the announcement bar + header + nav row that --header-height
  // alone misses. Recomputed each open; CSS keeps a calc() fallback.
  if (anyOpen) {
    const nav = document.querySelector('.navigation-container');
    if (nav) {
      const avail = Math.max(0, Math.floor(window.innerHeight - nav.getBoundingClientRect().bottom));
      document.documentElement.style.setProperty('--subnav-max-height', avail + 'px');
    }
  }
}

document.addEventListener('hover-menu:open', syncSubnavScrollLock);
document.addEventListener('hover-menu:close', syncSubnavScrollLock);

// Close all secondary submenus when clicking outside
document.addEventListener('click', (event) => {
  const openSubmenus = document.querySelectorAll('[data-hover-submenu][open]');
  openSubmenus.forEach(submenu => {
    if (!submenu.contains(event.target)) {
      submenu.removeAttribute('open');
      const trigger = submenu.querySelector('.secondary-submenu__trigger');
      const content = submenu.querySelector('[data-submenu-content]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (content) content.setAttribute('aria-hidden', 'true');
    }
  });
});

// Handle escape key globally
document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') {
    const openHoverMenus = document.querySelectorAll('hover-menu [data-hover-menu][open]');
    openHoverMenus.forEach(menu => {
      const hoverMenu = menu.closest('hover-menu');
      if (hoverMenu) {
        hoverMenu.close();
      }
    });
    
    // Also close secondary submenus
    const openSubmenus = document.querySelectorAll('[data-hover-submenu][open]');
    openSubmenus.forEach(submenu => {
      submenu.removeAttribute('open');
      const trigger = submenu.querySelector('.secondary-submenu__trigger');
      const content = submenu.querySelector('[data-submenu-content]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (content) content.setAttribute('aria-hidden', 'true');
    });
  }
});
